import React, { useMemo, useState } from 'react'
import classNames from 'classnames'
import { format, isThisYear } from 'date-fns'
import { Badge } from 'uiComponents/badge/badge'
import { LucideIcon } from 'utils/lucideIcon'
import { computeDiff, DiffEntry, summarize } from './diff'
import { AuditLogEntry, OBJECT_TYPE_LABELS, SOURCE_LABELS, STAFF_MASK } from './schema'

const formatWhen = (iso: string) => {
    const date = new Date(iso)
    return format(date, isThisYear(date) ? 'd MMM, HH:mm' : 'd MMM yyyy, HH:mm')
}

const actorLabel = (actor: string | null) => (actor === STAFF_MASK ? 'Convious staff' : actor ?? 'Unknown')

const DiffLine = ({ entry }: { entry: DiffEntry }) => (
    <div className="diff-line">
        <span className="diff-label">{entry.label}</span>
        {entry.kind === 'changed' && (
            <span className="diff-values">
                <span className="diff-before">{entry.before}</span>
                <span className="diff-arrow">→</span>
                <span className="diff-after">{entry.after}</span>
            </span>
        )}
        {entry.kind === 'added' && <span className="diff-chip added">{entry.after}</span>}
        {entry.kind === 'removed' && <span className="diff-chip removed">{entry.before}</span>}
    </div>
)

interface EntryRowProps {
    entry: AuditLogEntry
    onSelectObject: (objectId: string) => void
}

export const EntryRow = ({ entry, onSelectObject }: EntryRowProps) => {
    const [expanded, setExpanded] = useState(false)
    const diff = useMemo(() => computeDiff(entry.stateBefore, entry.stateAfter), [entry])
    const summary = summarize(diff, entry.action)
    const typeLabel = OBJECT_TYPE_LABELS[entry.objectType] ?? entry.objectType

    return (
        <div className={classNames('audit-entry', { expanded })}>
            <div className="audit-entry-row" onClick={() => setExpanded((v) => !v)}>
                <div className="audit-when" title={entry.createdAt}>
                    {formatWhen(entry.createdAt)}
                </div>
                <div className="audit-who">
                    <div title={actorLabel(entry.actor)}>{actorLabel(entry.actor)}</div>
                    <div className="audit-meta">
                        <span>{SOURCE_LABELS[entry.source] ?? entry.source}</span>
                        {entry.impersonatedBy && (
                            <Badge
                                variant="info"
                                size="small"
                                label={`via Convious · ${actorLabel(entry.impersonatedBy)}`}
                            />
                        )}
                    </div>
                </div>
                <div className="audit-what">
                    <span className="audit-type">{typeLabel} · </span>
                    <button
                        type="button"
                        className="audit-object-link"
                        title="Show only this object's history"
                        onClick={(e) => {
                            e.stopPropagation()
                            onSelectObject(entry.objectId)
                        }}
                    >
                        {entry.objectName || 'Unnamed'}
                    </button>
                    <span className={classNames('audit-action', entry.action)}> — {entry.action}</span>
                </div>
                <div className="audit-summary" title={summary}>
                    {summary || (entry.action === 'created' ? 'New' : 'Removed')}
                </div>
                <LucideIcon iconName="ChevronDown" className="audit-chevron" />
            </div>
            <div className="audit-entry-detail">
                <div className="audit-entry-detail-inner">
                    {entry.action === 'created' && <div className="diff-heading">Created with these values</div>}
                    {entry.action === 'deleted' && <div className="diff-heading">Last state before deletion</div>}
                    {diff.length === 0 ? (
                        <div className="diff-empty">No visible field changed.</div>
                    ) : (
                        diff.map((d, i) => <DiffLine key={i} entry={d} />)
                    )}
                </div>
            </div>
        </div>
    )
}
